import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { amPaymentsApi, type RaisePaymentInput } from '../api/amPayments.api';
import { extractApiError } from '../api/error';

interface Props {
  open: boolean;
  onClose: () => void;
  entityType: RaisePaymentInput['entityType'];
  entityId: number | null;
  title: string;
  defaultAmount?: number | null;
  amountLocked?: boolean; // rent: amount comes from the record
  onDone?: () => void;
}

/** Raises a payment request → starts Client Ops → … → Finance approval chain. */
export function RequestPaymentDialog({ open, onClose, entityType, entityId, title, defaultAmount, amountLocked, onDone }: Props) {
  const [amount, setAmount] = useState<string>('');
  const [purpose, setPurpose] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (open) { setAmount(defaultAmount != null ? String(defaultAmount) : ''); setPurpose(''); setError(null); } }, [open, defaultAmount]);

  const mut = useMutation({
    mutationFn: () => amPaymentsApi.raise({ entityType, entityId: entityId as number, amount: amount ? Number(amount) : undefined, purpose: purpose || undefined }),
    onSuccess: () => { onDone?.(); onClose(); },
    onError: (e) => setError(extractApiError(e)),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">Yeh request approval ke liye jayegi: Client Ops → Client Finance → Business Head → Finance.</Alert>
          <TextField label="Amount (₹)" type="number" size="small" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={amountLocked} helperText={amountLocked ? 'Rent amount record se aata hai' : 'Payable amount'} />
          <TextField label="Purpose / Note (optional)" size="small" multiline minRows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
          {!amountLocked && !amount && <Typography variant="caption" color="warning.main">Amount enter karein.</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending || (!amountLocked && !amount)} onClick={() => { setError(null); mut.mutate(); }}>Send for Approval</Button>
      </DialogActions>
    </Dialog>
  );
}
